import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TablePagination,
  CircularProgress,
  Alert,
  Grid,
  Card,
  CardContent,
  Tooltip
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  Visibility as ViewIcon,
  FilterList as FilterIcon
} from '@mui/icons-material';
import Layout from '../../components/Layout';
import apiService from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

interface Redencion {
  id: number;
  cupon_id: number;
  comercio_id: number;
  monto_aplicado: number;
  estado: 'pendiente' | 'aprobado' | 'rechazado';
  observaciones?: string;
  redencion_at: string;
  procesado_at?: string;
  tipo_documento?: string;
  numero_documento?: string;
  monto_consumo?: number;
  descuento_aplicado?: number;
  cupon_codigo?: string;
  cupon_tipo?: string;
  convenio_nombre?: string;
  convenio_descripcion?: string;
  comercio_nombre?: string;
}

interface Pagination {
  current_page: number;
  total_pages: number;
  total_items: number;
  items_per_page: number;
}

const ComercioRedenciones: React.FC = () => {
  const { user } = useAuth();
  const [redenciones, setRedenciones] = useState<Redencion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedRedencion, setSelectedRedencion] = useState<Redencion | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [processingId, setProcessingId] = useState<number | null>(null);
  const [observaciones, setObservaciones] = useState('');
  const [accion, setAccion] = useState<'aprobado' | 'rechazado'>('aprobado');
  
  // Filtros
  const [filtroEstado, setFiltroEstado] = useState<string>('');
  const [filtroTipoDoc, setFiltroTipoDoc] = useState<string>('');
  const [filtroNumDoc, setFiltroNumDoc] = useState<string>('');
  
  // Paginación
  const [pagination, setPagination] = useState<Pagination>({
    current_page: 1,
    total_pages: 0,
    total_items: 0,
    items_per_page: 20
  });

  const cargarRedenciones = async (page: number = 1, limit: number = 20) => {
    try {
      setLoading(true);
      setError(null);

      const params: any = {
        page,
        limit
      };

      if (filtroEstado) params.estado = filtroEstado;
      if (filtroTipoDoc) params.tipo_documento = filtroTipoDoc;
      if (filtroNumDoc) params.numero_documento = filtroNumDoc;

      // Agregar comercio_id si está disponible
      if (user?.id) params.comercio_id = user.id;

      const response = await apiService.getRedenciones(params);

      if (response.success) {
        setRedenciones(response.data.redenciones || []);
        setPagination(response.data.pagination);
      } else {
        setError(response.message || 'Error al cargar redenciones');
      }
    } catch (err: any) {
      console.error('Error cargando redenciones:', err);
      setError(err.response?.data?.message || 'Error al cargar redenciones');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargarRedenciones();
  }, [filtroEstado, filtroTipoDoc, filtroNumDoc]);

  const handleChangePage = (event: unknown, newPage: number) => {
    cargarRedenciones(newPage + 1, pagination.items_per_page);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newLimit = parseInt(event.target.value, 10);
    cargarRedenciones(1, newLimit);
  };

  const abrirDialogoProcesar = (redencion: Redencion, accion: 'aprobado' | 'rechazado') => {
    setSelectedRedencion(redencion);
    setAccion(accion);
    setObservaciones('');
    setDialogOpen(true);
  };

  const procesarRedencion = async () => {
    if (!selectedRedencion) return;

    try {
      setProcessingId(selectedRedencion.id);
      
      const response = await apiService.procesarRedencion(selectedRedencion.id, {
        estado: accion,
        observaciones: observaciones.trim() || undefined
      });

      if (response.success) {
        // Actualizar la redención en la lista
        setRedenciones(prev => 
          prev.map(r => 
            r.id === selectedRedencion.id 
              ? { ...r, estado: accion, observaciones: observaciones.trim() || r.observaciones }
              : r
          )
        );
        
        setDialogOpen(false);
        setSelectedRedencion(null);
      } else {
        setError(response.message || 'Error al procesar redención');
      }
    } catch (err: any) {
      console.error('Error procesando redención:', err);
      setError(err.response?.data?.message || 'Error al procesar redención');
    } finally {
      setProcessingId(null);
    }
  };

  const getEstadoColor = (estado: string) => {
    switch (estado) {
      case 'aprobado': return 'success';
      case 'rechazado': return 'error';
      case 'pendiente': return 'warning';
      default: return 'default';
    }
  };

  const formatearFecha = (fecha: string) => {
    return new Date(fecha).toLocaleString('es-PE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatearMonto = (monto: number | string) => {
    const numMonto = typeof monto === 'string' ? parseFloat(monto) : monto;
    return `S/ ${(isNaN(numMonto) ? 0 : numMonto).toFixed(2)}`;
  };

  if (loading && redenciones.length === 0) {
    return (
      <Layout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
      <Box>
        <Typography variant="h4" gutterBottom>
          Redenciones de Cupones
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Estadísticas rápidas */}
        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          <Card sx={{ flex: '1 1 200px', minWidth: 200 }}>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Redenciones
              </Typography>
              <Typography variant="h5">
                {pagination.total_items}
              </Typography>
            </CardContent>
          </Card>
          <Card sx={{ flex: '1 1 200px', minWidth: 200 }}>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Pendientes
              </Typography>
              <Typography variant="h5" color="warning.main">
                {redenciones.filter(r => r.estado === 'pendiente').length}
              </Typography>
            </CardContent>
          </Card>
          <Card sx={{ flex: '1 1 200px', minWidth: 200 }}>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Aprobadas
              </Typography>
              <Typography variant="h5" color="success.main">
                {redenciones.filter(r => r.estado === 'aprobado').length}
              </Typography>
            </CardContent>
          </Card>
          <Card sx={{ flex: '1 1 200px', minWidth: 200 }}>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Rechazadas
              </Typography>
              <Typography variant="h5" color="error.main">
                {redenciones.filter(r => r.estado === 'rechazado').length}
              </Typography>
            </CardContent>
          </Card>
        </Box>

        {/* Filtros */}
        <Paper sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <FilterIcon />
            <Box sx={{ minWidth: 200 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Estado</InputLabel>
                <Select
                  value={filtroEstado}
                  label="Estado"
                  onChange={(e) => setFiltroEstado(e.target.value)}
                >
                  <MenuItem value="">Todos</MenuItem>
                  <MenuItem value="pendiente">Pendiente</MenuItem>
                  <MenuItem value="aprobado">Aprobado</MenuItem>
                  <MenuItem value="rechazado">Rechazado</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ minWidth: 200 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Tipo Documento</InputLabel>
                <Select
                  value={filtroTipoDoc}
                  label="Tipo Documento"
                  onChange={(e) => setFiltroTipoDoc(e.target.value)}
                >
                  <MenuItem value="">Todos</MenuItem>
                  <MenuItem value="dni">DNI</MenuItem>
                  <MenuItem value="carnet_extranjeria">Carnet Extranjería</MenuItem>
                  <MenuItem value="pasaporte">Pasaporte</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ minWidth: 250, flex: 1 }}>
              <TextField
                fullWidth
                size="small"
                label="Número de Documento"
                value={filtroNumDoc}
                onChange={(e) => setFiltroNumDoc(e.target.value)}
                placeholder="Ej: 12345678"
              />
            </Box>
            <Button
              variant="outlined"
              onClick={() => {
                setFiltroEstado('');
                setFiltroTipoDoc('');
                setFiltroNumDoc('');
              }}
            >
              Limpiar
            </Button>
          </Box>
        </Paper>

        {/* Tabla de redenciones */}
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Fecha</TableCell>
                <TableCell>Cliente</TableCell>
                <TableCell>Convenio</TableCell>
                <TableCell>Cupón</TableCell>
                <TableCell align="right">Consumo</TableCell>
                <TableCell align="right">Descuento</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell>Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {redenciones.map((redencion) => (
                <TableRow key={redencion.id}>
                  <TableCell>{redencion.id}</TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatearFecha(redencion.redencion_at)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {redencion.tipo_documento?.toUpperCase()}: {redencion.numero_documento}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Tooltip title={redencion.convenio_descripcion || ''}>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                        {redencion.convenio_nombre}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {redencion.cupon_codigo}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">
                      {redencion.monto_consumo ? formatearMonto(redencion.monto_consumo) : '-'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" color="success.main">
                      {formatearMonto(redencion.monto_aplicado)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={redencion.estado.charAt(0).toUpperCase() + redencion.estado.slice(1)}
                      color={getEstadoColor(redencion.estado) as any}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {redencion.estado === 'pendiente' && (
                        <>
                          <Tooltip title="Aprobar">
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() => abrirDialogoProcesar(redencion, 'aprobado')}
                              disabled={processingId === redencion.id}
                            >
                              <CheckIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Rechazar">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => abrirDialogoProcesar(redencion, 'rechazado')}
                              disabled={processingId === redencion.id}
                            >
                              <CancelIcon />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                      <Tooltip title="Ver detalles">
                        <IconButton
                          size="small"
                          onClick={() => setSelectedRedencion(redencion)}
                        >
                          <ViewIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
              {redenciones.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    <Typography variant="body2" color="textSecondary" sx={{ py: 4 }}>
                      No se encontraron redenciones
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Paginación */}
        <TablePagination
          component="div"
          count={pagination.total_items}
          page={pagination.current_page - 1}
          onPageChange={handleChangePage}
          rowsPerPage={pagination.items_per_page}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 20, 50, 100]}
          labelRowsPerPage="Filas por página"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
        />

        {/* Dialog para procesar redención */}
        <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>
            {accion === 'aprobado' ? 'Aprobar' : 'Rechazar'} Redención #{selectedRedencion?.id}
          </DialogTitle>
          <DialogContent>
            {selectedRedencion && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Cliente: {selectedRedencion.tipo_documento?.toUpperCase()} {selectedRedencion.numero_documento}
                </Typography>
                <Typography variant="subtitle2" gutterBottom>
                  Convenio: {selectedRedencion.convenio_nombre}
                </Typography>
                <Typography variant="subtitle2" gutterBottom>
                  Monto: {formatearMonto(selectedRedencion.monto_aplicado)}
                </Typography>
                
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Observaciones"
                  value={observaciones}
                  onChange={(e) => setObservaciones(e.target.value)}
                  placeholder={
                    accion === 'aprobado' 
                      ? 'Observaciones adicionales (opcional)'
                      : 'Motivo del rechazo (recomendado)'
                  }
                  sx={{ mt: 2 }}
                />
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)}>
              Cancelar
            </Button>
            <Button
              onClick={procesarRedencion}
              variant="contained"
              color={accion === 'aprobado' ? 'success' : 'error'}
              disabled={processingId !== null}
              startIcon={processingId !== null ? <CircularProgress size={16} /> : null}
            >
              {accion === 'aprobado' ? 'Aprobar' : 'Rechazar'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Layout>
  );
};

export default ComercioRedenciones;
